// Types
import type {
  JobOperation,
} from '../../types'

import type {
  Job,
} from 'fc-model-sampling-jobs/types'

// Libs
import _map from 'lodash/map'
import _every from 'lodash/every'
import _size from 'lodash/size'

// Operator
import operator from '../operator'

// Controller
import controller from 'fc-ctrl-sampling-jobs'

// Effect Managers
import { call, fork, put, race, select, take } from 'redux-saga/effects'

// Sagas
import validateOperation from './validateOperation'

// Selectors
import { firstAvailableGrowerId$ } from 'fc-assets'

type OperationsArgs = {
  operations$: (state: Object) => Array<JobOperation>
}

// Exports
export default function *watchOperations ({operations$}: OperationsArgs): Generator<*, void, *> {
  while (true) {
    yield take([operator.actionTypes.add(), operator.actionTypes.edit(), operator.actionTypes.delete()])
    yield fork(handleOperations, {operations$})
  }
}

export function *handleOperations ({operations$}: OperationsArgs): Generator<*, void, *> {
  let done = false

  while (!done) {
    const effect = yield race({
      submit: take(operator.actionTypes.submitOperations()),
      cancel: take(operator.actionTypes.cancelOperations()),
    })

    if (effect.submit) {
      done = yield call(handleSubmitOperations, {operations$})
    } else {
      done = true // Cancelled
    }
  }
}

export function *handleSubmitOperations ({operations$}: OperationsArgs): Generator<*, boolean, *> {
  const operations = yield select(operations$)

  const nextOperations: Array<JobOperation> =
  yield _map(operations, function (operation: JobOperation): Object {
      return call(handleSubmitOperation, {
        ...operation,
        errors: [],
      })
    })

  return _every(nextOperations, function (operation: JobOperation): boolean {
    return operation.success
  })
}

export function *handleSubmitOperation (operation: JobOperation): Generator<*, JobOperation, *> {
  const growerId = yield select(firstAvailableGrowerId$)
  const validatedOperation = yield call(validateOperation, operation)

  if (_size(validatedOperation.errors)) {
    return validatedOperation
  }

  const model: Job = operation.model
  const proc = operation.operation === 'ADD'
    ? controller.create
    : operation.operation === 'EDIT'
      ? controller.update
      : controller.delete

  const { error, response } = yield call([controller, proc], {
    model: {...model.job},
    queryParams: {
      rootAsset: growerId,
    },
  })

  if (error) {
    const nextOperation = {
      ...operation,
      submitting: false,
      success: false,
      errors: response,
    }

    const action = operator.actionCreators.updateOperation(nextOperation)
    yield put(action)

    return nextOperation
  } else {
    const nextOperation = {
      ...operation,
      submitting: false,
      success: true,
    }

    const action = operator.actionCreators.cancelOperation(nextOperation)
    yield put(action)

    return nextOperation
  }
}
